import { useCallback, useEffect, useRef, useState } from 'react'
import { hasSystemRole, useAuthStore } from '../stores/authStore'
import {
  disconnectEcho,
  getEcho,
  isRealtimeConfigured,
  onRealtimeConnectionChange,
  personalChannel,
  realtimeConnectionStatus,
  realtimeEvents,
  releaseChannel,
  retainChannel,
  rideLifecycleEvents,
  staffChannels,
  type RealtimeStatus,
} from '../services/echo'

/**
 * Hooks temps réel du back-office (Laravel Echo sur Pusher).
 *
 * Toute la configuration de connexion vit dans `services/echo` — ces hooks ne
 * font que souscrire aux canaux privés déclarés par le backend et relayer les
 * événements vers les pages.
 */

export { disconnectEcho }

/** Conserve la dernière valeur d'un paramètre sans relancer les souscriptions. */
function useLatest<T>(value: T): { current: T } {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref
}

/** Identifiant de l'utilisateur connecté : rejoue les souscriptions à la connexion. */
function useAuthUserId(): number | null {
  const { user } = useAuthStore()

  return user?.id ?? null
}

/**
 * L'utilisateur connecté est-il chauffeur ? Miroir de
 * `NotificationSent::broadcastOn()` : le backend choisit `driver.{id}` pour un
 * chauffeur et `client.{id}` sinon (donc `client.{id}` pour le staff).
 */
function useAuthUserIsDriver(): boolean {
  const { user } = useAuthStore()

  return hasSystemRole(user, 'driver')
}

/**
 * État réel de la connexion Pusher, exposé aux indicateurs « temps réel ».
 *
 * Remplace l'ancien test `localStorage + VITE_PUSHER_APP_KEY`, qui affichait
 * « connecté » même sans WebSocket ouverte.
 */
export function useRealtimeConnection(): {
  status: RealtimeStatus
  isConnected: boolean
  isConfigured: boolean
} {
  const userId = useAuthUserId()
  const [status, setStatus] = useState<RealtimeStatus>(() => realtimeConnectionStatus())

  useEffect(() => {
    const unsubscribe = onRealtimeConnectionChange(setStatus)

    // Filet de sécurité : au premier rendu, la connexion peut ne pas exister
    // encore (aucune souscription ouverte) — on relit périodiquement l'état.
    const timer = window.setInterval(() => setStatus(realtimeConnectionStatus()), 5000)

    return () => {
      unsubscribe()
      window.clearInterval(timer)
    }
  }, [userId])

  return {
    status,
    isConnected: status === 'connected',
    isConfigured: isRealtimeConfigured(),
  }
}


// ─── Payloads des événements ──────────────────────────────────────────
// Ces interfaces décrivent les `broadcastWith()` du backend : les noms et
// structures correspondent exactement à `app/Events/*.php`.

/** `RideRequested` → `ride.requested`. */
export interface RideRequestedEvent {
  id: number
  pickup: { lat: number | null; lng: number | null; address: string | null }
  dropoff: { lat: number | null; lng: number | null; address: string | null }
  vehicle_type: string
  estimated_price: number | string | null
  distance_km: number | string | null
  duration_min: number | null
  client: { id: number; name: string; avatar: string | null; rating: number | null }
}

/**
 * `RideStatusUpdated`, `RideAccepted`, `RideCancelled`,
 * `RidePaymentInitiated`, `RidePaymentConfirmed` → `ride.*`.
 *
 * Le socle commun est `status` + l'identifiant de la course (`ride_id`, et
 * `id` pour `RideCancelled`) ; chaque événement ajoute ses propres champs
 * (chauffeur, véhicule, montant…).
 */
export interface RideStatusUpdatedEvent {
  ride_id?: number
  id?: number
  status: string
  updated_at?: string
  [key: string]: unknown
}

/** `DriverStatusUpdated` → `driver.status_updated` (canal `driver.{userId}`). */
export interface DriverStatusUpdatedEvent {
  driver_id: number
  status: string
  is_verified: boolean
  location: { lat: number | null; lng: number | null; last_updated_at: string | null }
  updated_at: string
}

/** `DriverLocationUpdated` → `driver.location_updated`. */
export interface DriverLocationUpdatedEvent {
  driver_id: number
  ride_id: number
  location: { lat: number; lng: number; heading: number | null }
  eta_minutes: number | null
  timestamp: string
}

/** `NotificationSent` → `notification.sent`. */
export interface NotificationSentEvent {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  category: string
  title: string
  message: string
  link?: string | null
  created_at: string | null
}

/**
 * `DashboardStatsUpdated` → `stats.updated` (canal `admin.dashboard`).
 *
 * Signal d'invalidation : le backend indique quel domaine a changé (`reason`,
 * aligné sur `App\Listeners\BroadcastDashboardStatsUpdated::REASONS`) et la
 * page relance les requêtes HTTP correspondantes.
 */
export interface StatsUpdatedEvent {
  reason: string
  ride_id: number | null
  driver_id: number | null
  timestamp: string
}

// Alias historiques conservés pour les pages consommatrices.
export type RideCreatedEvent = RideRequestedEvent
export type RideUpdatedEvent = RideStatusUpdatedEvent
export type DriverStatusEvent = DriverStatusUpdatedEvent
export type NotificationEvent = NotificationSentEvent

/** Options du hook principal : les événements `ride.*` sont traités ensemble. */
interface UseRealtimeAdminOptions {
  onRideCreated?: (event: RideCreatedEvent) => void
  onRideUpdated?: (event: RideUpdatedEvent) => void
  onDriverStatusChanged?: (event: DriverStatusEvent) => void
  onStatsUpdated?: (event: StatsUpdatedEvent) => void
}

/**
 * Souscriptions du back-office : courses, chauffeurs et agrégats.
 *
 * Canaux écoutés (déclarés pour le staff dans `routes/channels.php`) :
 *  - `admin.rides` → `ride.requested` + cycle de vie de la course ;
 *  - `admin.drivers` → `driver.status_updated` ;
 *  - `admin.dashboard` → `stats.updated` (`DashboardStatsUpdated`).
 *
 * Les notifications personnelles sont gérées par `useRealtimeNotifications` :
 * il n'existe aucun canal de notifications global au staff.
 */
export function useRealtimeAdmin(options: UseRealtimeAdminOptions = {}): {
  isConnected: boolean
  reconnect: () => void
} {
  const userId = useAuthUserId()
  const handlers = useLatest(options)
  const { isConnected } = useRealtimeConnection()
  const subscribedRef = useRef<string[]>([])

  const releaseAll = useCallback(() => {
    subscribedRef.current.forEach((channelName) => releaseChannel(channelName))

    subscribedRef.current = []
  }, [])

  const setupChannels = useCallback(() => {
    const echo = getEcho()
    if (!echo || !userId) return

    releaseAll()
    const subscribed: string[] = []

    // 1. Courses → `admin.rides`.
    const rides = echo.private(retainChannel(staffChannels.rides))
    rides.listen(realtimeEvents.rideRequested, (payload: RideRequestedEvent) => {
      handlers.current.onRideCreated?.(payload)
    })
    rideLifecycleEvents.forEach((event) => {
      rides.listen(event, (payload: RideStatusUpdatedEvent) => {
        handlers.current.onRideUpdated?.(payload)
      })
    })
    subscribed.push(staffChannels.rides)

    // 2. Chauffeurs → `admin.drivers`.
    echo
      .private(retainChannel(staffChannels.drivers))
      .listen(realtimeEvents.driverStatusUpdated, (payload: DriverStatusUpdatedEvent) => {
        handlers.current.onDriverStatusChanged?.(payload)
      })
    subscribed.push(staffChannels.drivers)

    // 3. Agrégats du tableau de bord → `admin.dashboard`
    //    (`App\Events\DashboardStatsUpdated`, émis à chaque changement du
    //    domaine : courses et chauffeurs).
    echo
      .private(retainChannel(staffChannels.dashboard))
      .listen(realtimeEvents.statsUpdated, (payload: StatsUpdatedEvent) => {
        handlers.current.onStatsUpdated?.(payload)
      })
    subscribed.push(staffChannels.dashboard)

    subscribedRef.current = subscribed
  }, [handlers, releaseAll, userId])

  const reconnect = useCallback(() => {
    // Libère les canaux puis ferme la connexion : `getEcho()` en ouvrira une
    // neuve à la prochaine souscription, avec le jeton courant.
    releaseAll()
    disconnectEcho()
    setupChannels()
  }, [releaseAll, setupChannels])

  useEffect(() => {
    setupChannels()

    return () => releaseAll()
  }, [releaseAll, setupChannels])

  return { isConnected, reconnect }
}

/**
 * Écoute une course précise sur le canal staff `admin.ride.{rideId}`
 * (`ride.accepted`, `ride.status_updated`, `ride.cancelled`, `ride.payment_*`)
 * ainsi que la position GPS du chauffeur (`driver.location_updated`, miroir
 * admin de `DriverLocationUpdated`).
 *
 * `rideId` doit être l'identifiant technique (clé primaire) diffusé par le
 * backend, et non l'UUID de l'URL : voir `RideResource.numeric_id`.
 */
export function useRealtimeRide(
  rideId: string | number | null,
  onUpdate?: (event: RideUpdatedEvent) => void,
  onDriverLocation?: (event: DriverLocationUpdatedEvent) => void,
): void {
  const userId = useAuthUserId()
  const handler = useLatest(onUpdate)
  const locationHandler = useLatest(onDriverLocation)

  useEffect(() => {
    if (!rideId) return

    const echo = getEcho()
    if (!echo) return

    const channelName = retainChannel(staffChannels.ride(rideId))
    const channel = echo.private(channelName)

    // `ride.requested` inclus : une fiche ouverte au moment où la demande est
    // diffusée doit se rafraîchir elle aussi.
    const watchedEvents = [realtimeEvents.rideRequested, ...rideLifecycleEvents]

    watchedEvents.forEach((event) => {
      channel.listen(event, (payload: RideStatusUpdatedEvent) => {
        handler.current?.(payload)
      })
    })

    channel.listen(realtimeEvents.driverLocationUpdated, (payload: DriverLocationUpdatedEvent) => {
      locationHandler.current?.(payload)
    })

    return () => releaseChannel(channelName)
  }, [handler, locationHandler, rideId, userId])
}

/**
 * Écoute un chauffeur précis sur le canal staff `admin.driver.{driverId}`
 * (`driver.status_updated` et `driver.location_updated`).
 *
 * `driverId` doit être l'identifiant technique du compte chauffeur (et non
 * l'UUID de l'URL) : voir `numeric_id` renvoyé par `Admin\DriverController`.
 */
export function useRealtimeDriver(
  driverId: string | number | null,
  onStatusChange?: (event: DriverStatusEvent) => void,
  onLocationChange?: (event: DriverLocationUpdatedEvent) => void,
): void {
  const userId = useAuthUserId()
  const handler = useLatest(onStatusChange)
  const locationHandler = useLatest(onLocationChange)

  useEffect(() => {
    if (!driverId) return

    const echo = getEcho()
    if (!echo) return

    const channelName = retainChannel(staffChannels.driver(driverId))
    echo
      .private(channelName)
      .listen(realtimeEvents.driverStatusUpdated, (payload: DriverStatusUpdatedEvent) => {
        handler.current?.(payload)
      })
      .listen(realtimeEvents.driverLocationUpdated, (payload: DriverLocationUpdatedEvent) => {
        locationHandler.current?.(payload)
      })

    return () => releaseChannel(channelName)
  }, [driverId, handler, locationHandler, userId])
}

/**
 * Liste des courses en temps réel (`admin.rides`) : nouvelles demandes et
 * changements d'état.
 *
 * Les callbacks sont conservés dans une référence : l'objet passé par la page
 * peut être recréé à chaque rendu sans provoquer de re-souscription.
 */
export function useRealtimeRides(callbacks: {
  onCreated?: (event: RideCreatedEvent) => void
  onUpdated?: (event: RideUpdatedEvent) => void
}): void {
  const userId = useAuthUserId()
  const handlers = useLatest(callbacks)

  useEffect(() => {
    const echo = getEcho()
    if (!echo) return

    const channelName = retainChannel(staffChannels.rides)
    const channel = echo.private(channelName)

    channel.listen(realtimeEvents.rideRequested, (payload: RideRequestedEvent) => {
      handlers.current.onCreated?.(payload)
    })
    rideLifecycleEvents.forEach((event) => {
      channel.listen(event, (payload: RideStatusUpdatedEvent) => {
        handlers.current.onUpdated?.(payload)
      })
    })

    return () => releaseChannel(channelName)
  }, [handlers, userId])
}

/**
 * Liste des chauffeurs en temps réel (`admin.drivers`).
 */
export function useRealtimeDrivers(
  onStatusChange?: (event: DriverStatusEvent) => void,
): void {
  const userId = useAuthUserId()
  const handler = useLatest(onStatusChange)

  useEffect(() => {
    const echo = getEcho()
    if (!echo) return

    const channelName = retainChannel(staffChannels.drivers)
    echo
      .private(channelName)
      .listen(realtimeEvents.driverStatusUpdated, (payload: DriverStatusUpdatedEvent) => {
        handler.current?.(payload)
      })

    return () => releaseChannel(channelName)
  }, [handler, userId])
}

/**
 * Notifications personnelles du membre du staff (`notification.sent`).
 *
 * `NotificationSent::broadcastOn()` ne cible que le canal personnel de son
 * destinataire (`driver.{id}` pour un chauffeur, `client.{id}` sinon — donc
 * `client.{id}` pour le staff). Il n'existe volontairement aucun canal de
 * notifications global : y router les notifications d'un utilisateur tiers les
 * rendrait lisibles par tout le back-office.
 *
 * Utilisé à la fois par le badge du layout et par la page Notifications : les
 * deux peuvent être montés en même temps (comptage de références des canaux).
 */
export function useRealtimeNotifications(
  onNotification?: (event: NotificationSentEvent) => void,
): void {
  const userId = useAuthUserId()
  const isDriverUser = useAuthUserIsDriver()
  const handler = useLatest(onNotification)

  useEffect(() => {
    if (!userId) return

    const echo = getEcho()
    if (!echo) return

    const channelName = retainChannel(personalChannel(userId, isDriverUser))
    echo
      .private(channelName)
      .listen(realtimeEvents.notificationSent, (payload: NotificationSentEvent) => {
        handler.current?.(payload)
      })

    return () => releaseChannel(channelName)
  }, [handler, isDriverUser, userId])
}

